import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Award, Settings } from 'lucide-react';
import { TikTokIcon } from '@/components/icons/TikTokIcon';
import { SmartLink } from '@/components/SmartLink';
import siteConfig from '@/config/siteConfig';
import { useClubInfo } from '@/context/ClubInfoContext';
import logoImage from '@/assets/logo.webp';

const LOGO_URL = logoImage;

export const Footer = () => {
  const { clubInfo } = useClubInfo();

  const quickLinks = [
    { path: '/mitgliedschaft', label: 'Mitglied werden' },
    { path: '/reitunterricht', label: 'Reitunterricht' },
    { path: '/aktivitaeten', label: 'Aktivitäten & Events' },
    { path: '/galerie', label: 'Bildergalerie' },
    { path: '/vorstand', label: 'Vorstand' },
    { path: '/sponsoren', label: 'Sponsoren' },
    { path: '/dokumente', label: 'Dokumente & Formulare' },
  ];

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Column 1: Vereinsinfo */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt={`${clubInfo.name} Logo`}
                className="h-12 w-12 object-contain"
                loading="lazy"
                decoding="async"
                width="400"
                height="400"
              />
              <div>
                <h3 className="font-display text-xl font-bold leading-tight" data-testid="footer-club-name">
                  Reit- und Fahrverein
                  <br />
                  Holle-Wüsting e.V.
                </h3>
                <p className="text-sm text-secondary-foreground/70">seit {clubInfo.foundingYear}</p>
              </div>
            </div>
            <p className="text-sm text-secondary-foreground/80 leading-relaxed">
              Seit {clubInfo.foundingYear} Ihre Adresse für Pferdesport in der Region. Tradition, Leidenschaft und
              Gemeinschaft.
            </p>
            <div className="flex items-center gap-2 bg-accent/20 rounded-lg p-3">
              <Award className="h-6 w-6 text-accent flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-accent">Ausgezeichnet</p>
                <p className="text-xs text-secondary-foreground/80">Engagementfreundlicher Sportverein in Gold</p>
              </div>
            </div>
          </div>

          {/* Column 2: Kontakt */}
          <div className="space-y-4">
            <h4 className="font-display text-lg font-semibold">Kontakt</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary-light flex-shrink-0 mt-0.5" />
                <div className="text-sm" data-testid="footer-address">
                  <p>{clubInfo.addressStreet}</p>
                  <p>
                    {clubInfo.addressZip} {clubInfo.addressCity}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-primary-light flex-shrink-0" />
                <a
                  href={`tel:${clubInfo.phone.replace(/\s/g, '')}`}
                  className="text-sm hover:text-primary-light transition-colors"
                  data-testid="footer-phone"
                >
                  {clubInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-primary-light flex-shrink-0" />
                <a href={`mailto:${clubInfo.email}`} className="text-sm hover:text-primary-light transition-colors">
                  {clubInfo.email}
                </a>
              </div>
            </div>
          </div>

          {/* Column 3: Quick Links */}
          <div className="space-y-4">
            <h4 className="font-display text-lg font-semibold">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-secondary-foreground/80 hover:text-primary-light transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Social Media */}
          <div className="space-y-4">
            <h4 className="font-display text-lg font-semibold">Folgen Sie uns</h4>
            <p className="text-sm text-secondary-foreground/80">Bleiben Sie über Social Media auf dem Laufenden!</p>

            <div className="pt-2">
              <div className="flex gap-3">
                <SmartLink
                  href={siteConfig.socialMedia.facebook}
                  showIcon={false}
                  ariaLabel="Facebook"
                  className="inline-flex items-center justify-center h-11 w-11 bg-secondary-foreground/10 rounded-lg hover:bg-primary transition-colors"
                >
                  <Facebook className="h-5 w-5" aria-hidden="true" />
                </SmartLink>
                <SmartLink
                  href={siteConfig.socialMedia.instagram}
                  showIcon={false}
                  ariaLabel="Instagram"
                  className="inline-flex items-center justify-center h-11 w-11 bg-secondary-foreground/10 rounded-lg hover:bg-pink-600 transition-colors"
                >
                  <Instagram className="h-5 w-5" aria-hidden="true" />
                </SmartLink>
                {siteConfig.socialMedia.tiktok && (
                  <SmartLink
                    href={siteConfig.socialMedia.tiktok}
                    showIcon={false}
                    ariaLabel="TikTok"
                    className="inline-flex items-center justify-center h-11 w-11 bg-secondary-foreground/10 rounded-lg hover:bg-black transition-colors"
                    data-testid="footer-tiktok-link"
                  >
                    <TikTokIcon className="h-5 w-5" aria-hidden="true" />
                  </SmartLink>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="border-t border-secondary-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-sm text-secondary-foreground/60">
                © {new Date().getFullYear()} {clubInfo.name}. Alle Rechte vorbehalten.
              </p>
              <p className="text-xs text-secondary-foreground/50 mt-1">
                Ehrenamtlich umgesetzt von Philip Nitsche
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/impressum"
                className="text-sm text-secondary-foreground/60 hover:text-secondary-foreground transition-colors"
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="text-sm text-secondary-foreground/60 hover:text-secondary-foreground transition-colors"
              >
                Datenschutz
              </Link>
              {/* Hidden Admin Link - klein, aber mit aria-label fuer Screenreader */}
              <Link
                to="/admin"
                aria-label="Admin-Bereich"
                title="Admin-Bereich"
                className="inline-flex items-center justify-center h-11 w-11 -mr-2 text-secondary-foreground/20 hover:text-secondary-foreground/40 transition-colors"
              >
                <Settings className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
